/**
 * Connect — sensor data layer (OMON-209 Phase A).
 *
 * Resolves an EpisodeOfCare to its IoT sensor streams. The traversal is a
 * faithful port of the legacy Flutter coordinator's `firestore_helpers.dart`
 * (`resolveObservationRefsFromEoC` + `traverseRefs` + `propertyTypeAndRefFromDeviceDoc`),
 * read in full 2026-06-23 — that app is the ONLY thing that resolves real-pilot
 * sensors, so we mirror it instead of re-inferring from data samples (which led
 * to three wrong models: `_instances` test manifests, empty `observation.device[]`,
 * and a bare `reason[]` chain). See `backlog/docs/OMON-209-coordinator-swimlane-spec.md`.
 *
 *   _episodeofcare/{eocDocId}
 *     → resolveObservationRefs:
 *         PREFER  EoC.extension[url == IOT_ANCHOR_URL].valueReference.reference → [observation]
 *         ELSE    EoC.reason[] (active) → _encounter ; _encounter.reason[] (active) → _observation
 *     → observation.device[] (active entries) → _device                  (traverseRefs)
 *     → _device.property[] (active, type in WHITELIST) → _values/{collectionId}
 *        (the _values doc must itself be active)
 *     → readings at _values/{collectionId}/values, keyed by deviceTimestamp
 *
 * IoT vs non-IoT is the observation SHAPE: an IoT observation has a POPULATED
 * `device[]`; the OneTouch auto-materialised device-registry observation has an
 * EMPTY one. `filterToIotEocs` uses that to narrow the connect client list to
 * IoT EoCs only (root cause of the "Pilot19 — no devices" dev report: a non-IoT
 * EoC was wrongly listed). Since OMON-282 that narrowing is ONE call to the
 * `coordinator_iot_clients` callable rather than a per-EoC sweep from the
 * browser; the rule itself lives in `functions/lib/iot_registry.py`.
 *
 * Why NOT reverse-query observation → EoC: verified in prod (2026-06-23) the
 * anchor observation's `subject` is an unpopulated template stub and its
 * `encounter[]` can point at a deleted encounter, so the EoC→observation link
 * is one-directional. Discovery must run forward from the EoC.
 */
import {
  doc,
  getDoc,
  type DocumentData,
  type DocumentReference,
} from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage, app } from "../firebase";
import type { PatientInfo } from "../types";

/** FHIR R5 extension URL anchoring an EoC to its IoT observation (Step 3a of the
 *  2026-05 encounter remodel). The preferred EoC→observation path. */
const IOT_ANCHOR_URL =
  "https://sweettreeconnect.com/fhir/StructureDefinition/iot-observation-anchor";

/**
 * The only device-property types that render (Flutter
 * `getPropertiesFromEOCRef`). Everything else a device exposes — INA260,
 * ENS160, MLX90640, thermalCamera, DS3231, HUSB238, I2CFRAM, ADS7830, LSM6DS,
 * CalibratedADS7830, deviceEventTriggerSensor, … — is dropped at the source.
 */
const SENSOR_WHITELIST = new Set([
  "BUTTONS",
  "analogSpanishmat",
  "HTS221",
  "analogPuckmat",
  "WIFI",
  "TSL2591",
  "pushNotificationSent",
  "calibratedPucks",
  "doorWindow",
  "motion",
  "temperatureHumidity",
  "powerPlug",
  // crash mat (gyromesh), 2026-11-01
  "matMotion",
  "matPresence",
  // Shelly Presence G4 (radar), 2026-11-01
  "bedState",
  "bedExit",
]);

/** A single sensor stream on a client's device — one swimlane row. */
export interface SensorLane {
  deviceId: string; // device.identifier, e.g. "stc_47d4db77d787" / "shl_7cc6..."
  deviceType: string; // device.type, e.g. "enterprise" | "powerPlug"
  location: string; // device.location, e.g. "Bedroom"
  deviceImage: string | null; // _deviceDefinition photo URL (OMON-215 gutter)
  deviceName: string; // _deviceDefinition registered name, e.g. "Smart Plug"
  propertyType: string; // the active property's chip type (HTS221, WIFI, …)
  valuesPath: string; // "_values/{collectionId}/values"
}

/** A device's display metadata for the swimlane gutter (OMON-215). */
interface DeviceMeta {
  image: string | null;
  name: string;
}

/** Extract the bucket object path from a `_deviceDefinition` photo URL. The
 *  stored form is `https://storage.googleapis.com/{bucket}/{path}?...`; we also
 *  accept the Firebase `…/o/{encoded path}` form defensively. */
function storageObjectPath(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    if (u.hostname === "storage.googleapis.com") {
      const parts = u.pathname.replace(/^\/+/, "").split("/");
      parts.shift(); // drop the bucket segment
      return parts.length ? decodeURIComponent(parts.join("/")) : null;
    }
    const m = u.pathname.match(/\/o\/(.+)$/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/**
 * OMON-217: resolve a device photo to a usable image URL. The catalogue bucket
 * blocks public access, so the raw `storage.googleapis.com` URL stored on the
 * DeviceDefinition 403s in a plain <img>. Re-resolve via the authenticated
 * Storage SDK (getDownloadURL → tokenised URL the canvas can load). Returns null
 * on any failure (object missing / rules deny) → the gutter shows an initial.
 */
async function resolveDevicePhoto(rawUrl: string): Promise<string | null> {
  const path = storageObjectPath(rawUrl);
  if (!path) return null;
  try {
    return await getDownloadURL(storageRef(storage, path));
  } catch {
    return null;
  }
}

/**
 * Resolve a device's `_deviceDefinition` → its photo URL + registered name for
 * the swimlane's frozen device gutter (OMON-215). The definition doc id is the
 * deviceType (e.g. `_deviceDefinition/powerPlug`); the photo lives at
 * `property[devicePhoto].valueAttachment.url`. Cached per deviceType (a client's
 * two H&T sensors share one definition → one read). Best-effort: a missing photo
 * just yields a null image (the gutter falls back to an initial).
 */
async function loadDeviceMeta(
  deviceType: string,
  deviceSnap: { get(field: string): unknown },
  cache: Map<string, DeviceMeta>,
): Promise<DeviceMeta> {
  const cached = cache.get(deviceType);
  if (cached) return cached;

  let meta: DeviceMeta = { image: null, name: deviceType };
  try {
    const defRef = deviceSnap.get("definition");
    const defSnap = isReference(defRef)
      ? await getDoc(defRef)
      : await getDoc(doc(db, "_deviceDefinition", deviceType));
    if (defSnap.exists()) {
      const props = defSnap.get("property");
      let rawUrl: string | null = null;
      if (Array.isArray(props)) {
        for (const p of props) {
          const code = p?.type?.coding?.[0]?.code;
          const url = p?.valueAttachment?.url;
          if (code === "devicePhoto" && typeof url === "string") {
            rawUrl = url;
            break;
          }
        }
      }
      // OMON-217: the stored URL isn't publicly fetchable — resolve a tokenised
      // download URL through the authenticated Storage SDK.
      const image = rawUrl ? await resolveDevicePhoto(rawUrl) : null;
      const nameEntry = defSnap.get("deviceName");
      const name =
        Array.isArray(nameEntry) && typeof nameEntry[0]?.name === "string"
          ? nameEntry[0].name
          : deviceType;
      meta = { image, name };
    }
  } catch {
    // Best-effort — gutter falls back to an initial + the deviceType.
  }
  cache.set(deviceType, meta);
  return meta;
}

function isReference(v: unknown): v is DocumentReference {
  return (
    typeof v === "object" &&
    v !== null &&
    "path" in v &&
    typeof (v as { path: unknown }).path === "string"
  );
}

/** Placeholder value-collection refs that carry no real stream. */
function isTemplatePath(path: string): boolean {
  return path.includes("/template_") || path.endsWith("/_");
}

/**
 * Port of Flutter `traverseRefs(dR, field, 'reference')`: the parent doc must be
 * `active === true`; each array entry under `field` must itself be active and
 * carry a `reference` whose target document EXISTS. Returns the surviving refs.
 *
 * `parentData` lets a caller that has already read the parent skip the re-read
 * (we read the EoC/observation once for both the gate and the array).
 */
async function traverseRefs(
  parentRef: DocumentReference,
  field: string,
  parentData?: DocumentData,
): Promise<DocumentReference[]> {
  let data = parentData;
  if (!data) {
    const snap = await getDoc(parentRef);
    if (!snap.exists()) return [];
    data = snap.data();
  }
  if (!data || data.active !== true) return []; // Flutter: null/false → []
  const arr = data[field];
  if (!Array.isArray(arr)) return [];

  const candidates: DocumentReference[] = [];
  for (const entry of arr) {
    if (!entry || typeof entry !== "object") continue;
    if (entry.active !== true) continue;
    const ref = entry.reference;
    if (!isReference(ref)) continue;
    candidates.push(ref);
  }
  // OMON-351: the existence checks run together, not one round trip each.
  // Order is preserved (Promise.all keeps input order), and so is failure:
  // one rejected read still rejects the whole traversal, as the loop did.
  const exists = await Promise.all(candidates.map(async (ref) => (await getDoc(ref)).exists()));
  return candidates.filter((_, i) => exists[i]);
}

/**
 * Resolve every active Observation reachable from an EoC. Extension-anchor
 * first (single observation), legacy reason→encounter→reason chain as the
 * fallback. Both shapes coexist during the encounter-remodel migration.
 */
export async function resolveObservationRefs(
  eocDocId: string,
): Promise<DocumentReference[]> {
  const eocRef = doc(db, "_episodeofcare", eocDocId);
  const eocSnap = await getDoc(eocRef);
  if (!eocSnap.exists()) return [];
  const eocData = eocSnap.data();

  // Preferred: the iot-observation-anchor extension. (No EoC.active gate here —
  // mirrors the Flutter extension branch, which reads the extension directly.)
  const extensions = eocData.extension;
  if (Array.isArray(extensions)) {
    for (const ext of extensions) {
      if (ext && typeof ext === "object" && ext.url === IOT_ANCHOR_URL) {
        const ref = ext.valueReference?.reference;
        if (isReference(ref)) return [ref];
      }
    }
  }

  // Fallback: EoC.reason[] → encounters ; encounter.reason[] → observations.
  // Pass the already-read EoC data so traverseRefs doesn't re-read it.
  const encounterRefs = await traverseRefs(eocRef, "reason", eocData);
  // OMON-351: each encounter's traversal in parallel; flattened in encounter
  // order, so the result is identical to the sequential loop's.
  const perEncounter = await Promise.all(encounterRefs.map((encRef) => traverseRefs(encRef, "reason")));
  return perEncounter.flat();
}

/**
 * Resolve an EoC doc-id to its active, whitelisted sensor lanes via
 * `observation.device[]`. Returns [] for any EoC with no IoT observation (the
 * common case — only the ~5 live IoT pilots resolve to devices).
 */
export async function loadSensorLanes(eocDocId: string): Promise<SensorLane[]> {
  const observationRefs = await resolveObservationRefs(eocDocId);
  if (observationRefs.length === 0) return [];

  const lanes: SensorLane[] = [];
  const seenLaneKeys = new Set<string>();
  const metaCache = new Map<string, DeviceMeta>();

  for (const obsRef of observationRefs) {
    // traverseRefs gates obs.active and each device entry's active + existence.
    const deviceRefs = await traverseRefs(obsRef, "device");
    // 2026-07-08 perf review D4: resolve devices CONCURRENTLY (device doc +
    // definition meta + storage photo per device were strictly serial awaits —
    // view-open latency scaled linearly with device count). Order is preserved
    // by index so lane order is unchanged.
    const resolved = await mapWithConcurrency(deviceRefs, 6, async (deviceRef) => {
      const deviceSnap = await getDoc(deviceRef);
      if (!deviceSnap.exists() || deviceSnap.get("active") !== true) return null;

      const deviceId = String(deviceSnap.get("identifier") ?? deviceRef.id);
      const deviceType = String(deviceSnap.get("type") ?? "unknown");
      const location = String(deviceSnap.get("location") ?? "—");
      const meta = await loadDeviceMeta(deviceType, deviceSnap, metaCache);

      const props = deviceSnap.get("property");
      if (!Array.isArray(props)) return null;
      const candidates: SensorLane[] = [];
      for (const prop of props) {
        if (!prop || prop.active !== true) continue;
        const propType = typeof prop.type === "string" ? prop.type : "";
        if (!SENSOR_WHITELIST.has(propType)) continue;
        const ref = prop.reference;
        if (!isReference(ref) || isTemplatePath(ref.path)) continue;

        // Deliberately DON'T read the `_values` doc. Readings now come from
        // BigQuery (keyed by device.identifier — see sensor-series.ts), so the
        // registry only needs device+property `active` + the whitelist. Reading
        // `_values` was a Flutter-era holdover: it's pure latency in prod
        // (verified: 10 lanes resolve with or without it) and it BREAKS dev,
        // where the prod→dev clone deliberately skips the 8M+ `_values` docs —
        // the gate then dropped every property → "no sensors".
        candidates.push({
          deviceId,
          deviceType,
          location,
          deviceImage: meta.image,
          deviceName: meta.name,
          propertyType: propType,
          valuesPath: `${ref.path}/values`,
        });
      }
      return candidates;
    });
    // De-dup OUTSIDE the concurrent mapper (shared-set mutation inside would
    // race); order is preserved by mapWithConcurrency, so lane order and the
    // "first EoC wins" de-dup semantics are unchanged. De-dup the same physical
    // sensor (shared across EoCs) by device + property type.
    for (const candidates of resolved) {
      if (!candidates) continue;
      for (const cand of candidates) {
        const key = `${cand.deviceId}::${cand.propertyType}`;
        if (seenLaneKeys.has(key)) continue;
        seenLaneKeys.add(key);
        lanes.push(cand);
      }
    }
  }
  return lanes;
}

// OMON-282: `eocHasActiveDevices` (the per-EoC client-side discriminator) was
// deleted here, not just left unused. It is the ONLY caller that has gone away,
// and keeping a second, LOOSER copy of the IoT rule in the codebase is how the
// coordinator overview and the family surface drift apart again — it tested the
// observation's `device[]` entry but never the device document's own `active`.
// The single implementation now lives server-side in
// `functions/lib/iot_registry.py`, exercised by both surfaces.

/** Run `fn` over `items` with at most `concurrency` in flight. Preserves order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  async function worker() {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, worker);
  await Promise.all(workers);
  return out;
}

// OMON-282: an ACTION on coordinator_sensors, not its own function. In
// Functions v2 each exported function is its own Cloud Run service, so a warm
// instance is per-function — two callables on one surface meant two always-on
// bills, each (below 1 vCPU) serving a single request at a time. Same
// action-typed envelope as practitioner_role_action / agent.fhir's dispatcher.
const iotClientsCallable = httpsCallable<
  { action: "iot-clients"; eocIds: string[] },
  { iotEocIds: string[] }
>(getFunctions(app, "europe-west2"), "coordinator_sensors");

/**
 * Narrow a patient list to IoT EoCs — the clients with at least one active
 * device — via ONE call to `coordinator_iot_clients` (OMON-282).
 *
 * This used to walk EoC → anchor observation → device[] in the BROWSER, one
 * client at a time at 8-way concurrency: ~580 round trips across 291 EoCs to
 * find the ~5 that qualify. Measured ~7s on office wifi. The cost is
 * round-TRIP-count rather than bytes, so on cellular (200–300ms RTT) the same
 * code took 20–30s — the network a coordinator actually has when out of the
 * building. Server-side the identical walk is three batched reads inside the
 * datacentre.
 *
 * ONE deliberate behaviour change: the server also requires the DEVICE
 * DOCUMENT to be active, not merely the observation's `device[]` entry. The
 * old client-side check tested the entry alone, so a client whose devices had
 * all been retired could still be listed — which is the exact failure this
 * filter was added to prevent ("Pilot19 — no registered devices"). It also
 * makes the coordinator overview agree with the family surface, which has
 * always used the stricter rule (`_family_device_registry`). 5 of 88 devices
 * are currently retired, so this can change the list; that is the point.
 *
 * Fails LOUD: if the call fails the caller sees the error rather than an
 * empty or silently-stale list. A fallback to the old client-side sweep would
 * hide a broken deploy behind 30 seconds of apparent success.
 */
export async function filterToIotEocs(
  patients: PatientInfo[],
): Promise<PatientInfo[]> {
  if (patients.length === 0) return [];
  const res = await iotClientsCallable({
    action: "iot-clients",
    eocIds: patients.map((p) => p.docId),
  });
  const iot = new Set(res.data?.iotEocIds ?? []);
  return patients.filter((p) => iot.has(p.docId));
}
